import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { GeneratorTopics, GeneratorDefaultsActions, ChangedPill } from '../components/roster/GeneratorLevels';
import { berlinDateTime, buildTimeBerlin } from '../hooks/useAppVersion';
import NotificationSettings from '../components/admin/NotificationSettings';

// ── Audit Log Tab ─────────────────────────────────────────────────────────────

function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: '', action: '', date_from: '', date_to: '' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p = page, f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p });
      if (f.user_id) params.set('user_id', f.user_id);
      if (f.action) params.set('action', f.action);
      if (f.date_from) params.set('date_from', f.date_from);
      if (f.date_to) params.set('date_to', f.date_to);
      const res = await api.get(`/admin/audit-log?${params}`);
      setEntries(res.data.entries);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      toast.error('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(1, filters); }, []); // eslint-disable-line

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const applyFilters = () => {
    setPage(1);
    load(1, filters);
  };

  const changePage = (p) => {
    setPage(p);
    load(p, filters);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  };

  const fmtDetails = (d) => {
    if (!d) return '';
    const str = JSON.stringify(d);
    return str.length > 80 ? str.slice(0, 80) + '…' : str;
  };

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-input"
              value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-input"
              value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">User</label>
            <select
              className="form-select"
              value={filters.user_id}
              onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))}
            >
              <option value="">All users</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Action contains</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. sickness"
              value={filters.action}
              onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <button className="btn btn-primary" onClick={applyFilters}>Filter</button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No entries found.</td></tr>
                )}
                {entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{fmtDate(e.created_at)}</td>
                    <td>
                      {e.user_name
                        ? <><span style={{ fontWeight: 500 }}>{e.user_name}</span><br /><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{e.user_email}</span></>
                        : <span style={{ color: 'var(--text-muted)' }}>System</span>
                      }
                    </td>
                    <td><code style={{ fontSize: '0.8rem', background: 'var(--primary-xlight)', padding: '2px 6px', borderRadius: '4px' }}>{e.action}</code></td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
                      {e.target_type ? `${e.target_type}${e.target_id ? ` #${e.target_id}` : ''}` : '—'}
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-light)', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {fmtDetails(e.details)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
              {total} entries · Page {page} of {pages}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => changePage(page - 1)}>Previous</button>
              <button className="btn btn-secondary btn-sm" disabled={page >= pages} onClick={() => changePage(page + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Roster generator card (superadmin Settings tab and the admin view) ─────────

// Levels + block / variety numbers (GET /shifts/generator-settings), one Save
function RosterGeneratorCard() {
  const [genMeta, setGenMeta] = useState(null);
  const [levels, setLevels] = useState(null);
  const [genNumbers, setGenNumbers] = useState(null);
  const loadGenerator = () => api.get('/shifts/generator-settings')
    .then((r) => { setGenMeta(r.data); setLevels(r.data.levels); setGenNumbers(r.data.numbers); })
    .catch(() => toast.error('Failed to load the generator settings.'));

  useEffect(() => { loadGenerator(); }, []); // eslint-disable-line

  return (
    <div className="card" style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Roster Generator</h3>
      <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
        How strongly each goal counts when the generator picks a person. <strong>Normal</strong> is the standard
        behaviour; Low and High make a goal count less or more, Off ignores it. Changes apply to the next generation.
      </p>
      <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Never affected: legal working-time limits, availability, vacation, qualifications and contract minimums.
        Admins can try other levels for a single draft in the Generate dialog without changing these settings.
      </p>
      {genMeta && levels && genNumbers ? (
        <>
          <GeneratorTopics
            meta={genMeta}
            levels={levels}
            onLevels={setLevels}
            numbers={genNumbers}
            onNumbers={setGenNumbers}
            levelBaseline={genMeta.factory_levels}
            numberBaseline={genMeta.factory_numbers}
            idPrefix="admin"
          />
          <GeneratorDefaultsActions
            meta={genMeta}
            levels={levels}
            numbers={genNumbers}
            onSaved={() => loadGenerator()}
          />
        </>
      ) : (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</p>
      )}
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  const [botToken, setBotToken] = useState('');
  const [botActive, setBotActive] = useState(false);
  const [botUsername, setBotUsername] = useState('');
  const [smtp, setSmtp] = useState({ SMTP_HOST: '', SMTP_PORT: '465', SMTP_USER: '', SMTP_PASS: '', SMTP_FROM: '' });
  const [appSettings, setAppSettings] = useState({ FRONTEND_URL: '', BUSINESS_ADDRESS: '' });
  const [newHireShifts, setNewHireShifts] = useState('2.5');
  const [reminders, setReminders] = useState({ time_reminders_enabled: true, time_reminders_digest_enabled: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    api.get('/admin/settings').then(res => {
      const s = res.data.settings || {};
      setBotToken(s.TELEGRAM_BOT_TOKEN ? '••••••••' : '');
      setBotActive(res.data.bot_active || false);
      setBotUsername(res.data.bot_username || '');
      setSmtp({
        SMTP_HOST: s.SMTP_HOST || '',
        SMTP_PORT: s.SMTP_PORT || '465',
        SMTP_USER: s.SMTP_USER || '',
        SMTP_PASS: s.SMTP_PASS ? '••••••••' : '',
        SMTP_FROM: s.SMTP_FROM || '',
      });
      setAppSettings({
        FRONTEND_URL: s.FRONTEND_URL || '',
        BUSINESS_ADDRESS: s.BUSINESS_ADDRESS || '',
      });
      setNewHireShifts(s.NEW_HIRE_SHIFTS || '2.5');
      // Absent = on
      setReminders({
        time_reminders_enabled: s.time_reminders_enabled !== 'false',
        time_reminders_digest_enabled: s.time_reminders_digest_enabled !== 'false',
      });
    }).catch(() => toast.error('Failed to load settings.'));
  }, []); // eslint-disable-line

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      await api.post('/admin/settings', { settings: patch });
      toast.success('Settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const saveTelegram = () => {
    if (!botToken || botToken === '••••••••') {
      toast('Enter a new token to update.');
      return;
    }
    saveSettings({ TELEGRAM_BOT_TOKEN: botToken });
  };

  const saveSmtp = () => {
    const patch = {};
    for (const [k, v] of Object.entries(smtp)) {
      if (v && v !== '••••••••') patch[k] = v;
    }
    saveSettings(patch);
  };

  const saveApp = () => {
    saveSettings({
      FRONTEND_URL: appSettings.FRONTEND_URL,
      BUSINESS_ADDRESS: appSettings.BUSINESS_ADDRESS.trim(),
    });
  };

  const saveNewHire = () => saveSettings({ NEW_HIRE_SHIFTS: String(newHireShifts) });

  // Reminder toggles save immediately
  const toggleReminder = (key, value) => {
    setReminders(r => ({ ...r, [key]: value }));
    saveSettings({ [key]: String(value) });
  };

  const testTelegram = async () => {
    setTesting('telegram');
    try {
      await api.post('/admin/settings/test-telegram');
      toast.success('Test message sent to your Telegram!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test failed.');
    } finally {
      setTesting('');
    }
  };

  const testEmail = async () => {
    setTesting('email');
    try {
      await api.post('/admin/settings/test-email');
      toast.success(`Test email sent to ${user?.email}!`);
    } catch {
      toast.error('Failed to send test email.');
    } finally {
      setTesting('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Telegram Settings */}
      <div className="card" style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Telegram Settings</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          Configure the Telegram bot for shift notifications.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: botActive ? 'var(--success)' : 'var(--text-muted)',
            flexShrink: 0,
          }} />
          <span style={{ fontSize: '0.85rem', color: botActive ? 'var(--success)' : 'var(--text-muted)' }}>
            {botActive ? `Bot active (@${botUsername})` : 'Not connected'}
          </span>
        </div>

        <div className="form-group">
          <label className="form-label">Bot Token</label>
          <input
            type="password"
            className="form-input"
            placeholder="Enter token from @BotFather"
            value={botToken}
            onFocus={e => { if (e.target.value === '••••••••') setBotToken(''); }}
            onChange={e => setBotToken(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-primary btn-sm" onClick={saveTelegram} disabled={saving}>
            {saving ? 'Saving…' : 'Save Token'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={testTelegram}
            disabled={!botActive || testing === 'telegram'}
          >
            {testing === 'telegram' ? 'Sending…' : 'Test bot'}
          </button>
        </div>
      </div>

      {/* SMTP Settings */}
      <div className="card" style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Email / SMTP Settings</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          Used for password resets and monthly report delivery only.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          {[
            { key: 'SMTP_HOST', label: 'SMTP Host', placeholder: 'smtp.example.com' },
            { key: 'SMTP_PORT', label: 'SMTP Port', placeholder: '465' },
            { key: 'SMTP_USER', label: 'SMTP Username', placeholder: 'user@example.com' },
            { key: 'SMTP_FROM', label: 'From Address', placeholder: 'Roster <noreply@example.com>' },
          ].map(({ key, label, placeholder }) => (
            <div className="form-group" key={key} style={{ marginBottom: 0 }}>
              <label className="form-label">{label}</label>
              <input
                type="text"
                className="form-input"
                placeholder={placeholder}
                value={smtp[key]}
                onChange={e => setSmtp(s => ({ ...s, [key]: e.target.value }))}
              />
            </div>
          ))}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">SMTP Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Enter password"
              value={smtp.SMTP_PASS}
              onFocus={e => { if (e.target.value === '••••••••') setSmtp(s => ({ ...s, SMTP_PASS: '' })); }}
              onChange={e => setSmtp(s => ({ ...s, SMTP_PASS: e.target.value }))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-primary btn-sm" onClick={saveSmtp} disabled={saving}>
            {saving ? 'Saving…' : 'Save SMTP'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={testEmail}
            disabled={testing === 'email'}
          >
            {testing === 'email' ? 'Sending…' : 'Send test email'}
          </button>
        </div>
      </div>

      <RosterGeneratorCard />

      {/* Staffing / hiring recommendation */}
      <div className="card" style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Staffing / hiring recommendation</h3>
        <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
          Used by the staffing check to say how many people to hire for yellow and red months.
        </p>
        <div className="form-group" style={{ maxWidth: '320px' }}>
          <label className="form-label" htmlFor="new-hire-shifts">
            Typical new hire: preferred shifts per week
            {Number(newHireShifts) !== 2.5 && <ChangedPill title="Factory value: 2.5" />}
          </label>
          <input
            id="new-hire-shifts"
            type="number"
            inputMode="decimal"
            className="form-input"
            style={{ width: '100%', minHeight: '44px' }}
            min="0.5"
            max="7"
            step="0.5"
            value={newHireShifts}
            onChange={e => setNewHireShifts(e.target.value)}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Factory value 2.5. Between 0.5 and 7, in steps of 0.5.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={saveNewHire} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* App Settings */}
      <div className="card" style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>App Settings</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          General application configuration.
        </p>

        <div className="form-group">
          <label className="form-label">Frontend URL</label>
          <input
            type="url"
            className="form-input"
            placeholder="https://roster.yourdomain.com"
            value={appSettings.FRONTEND_URL}
            onChange={e => setAppSettings(s => ({ ...s, FRONTEND_URL: e.target.value }))}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Used to build links inside Telegram notifications.
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Business address</label>
          <input
            type="text"
            className="form-input"
            placeholder="Street 1, 12345 Berlin"
            maxLength={200}
            value={appSettings.BUSINESS_ADDRESS}
            onChange={e => setAppSettings(s => ({ ...s, BUSINESS_ADDRESS: e.target.value }))}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Shown as the location of shifts in staff calendar subscriptions.
          </p>
        </div>

        <button className="btn btn-primary btn-sm" onClick={saveApp} disabled={saving}>
          {saving ? 'Saving…' : 'Save App Settings'}
        </button>
      </div>

      {/* Time tracking reminders */}
      <div className="card" style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Time Tracking Reminders</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          Staff get a reminder 15 minutes after a shift ends if they haven't logged their time,
          and a second one the next day at 10:00. No reminders between 00:00 and 08:00.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={reminders.time_reminders_enabled}
              disabled={saving}
              onChange={e => toggleReminder('time_reminders_enabled', e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--primary-dark)' }}
            />
            Time tracking reminders
          </label>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', marginLeft: '26px',
            cursor: reminders.time_reminders_enabled ? 'pointer' : 'not-allowed',
            opacity: reminders.time_reminders_enabled ? 1 : 0.5,
          }}>
            <input
              type="checkbox"
              checked={reminders.time_reminders_digest_enabled}
              disabled={saving || !reminders.time_reminders_enabled}
              onChange={e => toggleReminder('time_reminders_digest_enabled', e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--primary-dark)' }}
            />
            Weekly admin digest
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              (Mondays 09:00: last week's shifts that are still not logged)
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

// ── SystemInfoTab ──────────────────────────────────────────────────────────────

function SystemInfoTab() {
  const [backend, setBackend] = useState(null);
  const [frontend, setFrontend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/version'),
      fetch('/version.json').then((r) => r.json()),
    ]).then(([beRes, feRes]) => {
      if (beRes.status === 'fulfilled') setBackend(beRes.value.data);
      if (feRes.status === 'fulfilled') setFrontend(feRes.value);
      setLoading(false);
    });
  }, []);

  const mismatch = backend && frontend && backend.commit !== frontend.commit && backend.commit !== 'unknown' && frontend.commit !== 'unknown';

  const row = (label, value) => (
    <div style={{ display: 'flex', gap: '16px', padding: '8px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      <span style={{ width: '160px', flexShrink: 0, fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '0.88rem', color: 'var(--text)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{value || '—'}</span>
    </div>
  );

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {mismatch && (
        <div style={{ padding: '12px 16px', background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)', fontSize: '0.875rem', color: '#7c4a00', fontWeight: 500 }}>
          ⚠ Frontend and backend versions differ — a rebuild may have failed. Run <code style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: '3px' }}>bash update.sh</code> on the server to rebuild both.
        </div>
      )}

      <div className="card">
        <h3 style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '12px' }}>Backend</h3>
        {row('Commit', backend?.commit)}
        {row('Commit date', backend?.commit_date)}
        {row('Commit time (Berlin)', berlinDateTime(backend?.commit_time))}
        {row('Commit message', backend?.commit_message)}
        {row('Build date', backend?.build_date)}
        {row('Built on server (Berlin)', buildTimeBerlin(backend))}
      </div>

      <div className="card">
        <h3 style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '12px' }}>Frontend</h3>
        {row('Commit', frontend?.commit)}
        {row('Commit date', frontend?.commit_date)}
        {row('Commit time (Berlin)', berlinDateTime(frontend?.commit_time))}
        {row('Commit message', frontend?.commit_message)}
        {row('Build date', frontend?.build_date)}
        {row('Built on server (Berlin)', buildTimeBerlin(frontend))}
      </div>
    </div>
  );
}

// ── AdminPage ─────────────────────────────────────────────────────────────────

// Superadmin: full panel. Admin: only the Roster generator card (same URL).
export default function AdminPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('audit');

  useEffect(() => {
    if (!isAdmin) navigate('/roster', { replace: true });
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  if (!isSuperAdmin) {
    return (
      <div>
        <div className="page-header" style={{ marginBottom: '20px' }}>
          <div>
            <h1 className="page-title">Admin</h1>
            <p className="page-subtitle">Roster generator defaults and notifications</p>
          </div>
        </div>
        <RosterGeneratorCard />
        <div style={{ marginTop: '20px' }}>
          <NotificationSettings />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">Admin Panel</h1>
          <p className="page-subtitle">Audit log, settings, and system configuration</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid var(--border)', paddingBottom: '0', overflowX: 'auto' }}>
        {[
          { key: 'audit', label: 'Audit Log' },
          { key: 'settings', label: 'Settings' },
          { key: 'notifications', label: 'Notifications' },
          { key: 'system', label: 'System Info' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
            style={{ marginBottom: '-2px' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'audit' && <AuditLogTab />}
      {tab === 'settings' && <SettingsTab />}
      {tab === 'notifications' && <NotificationSettings />}
      {tab === 'system' && <SystemInfoTab />}
    </div>
  );
}
